/**
 * La mappa di CENSO: una tela sola per topologia, correlazione e servizi.
 *
 * ## Le scelte che la fanno funzionare
 *
 * - **Altezza fissa, comandi dentro.** La tela ha la sua altezza e i comandi
 *   (vista, zoom, adatta, schermo intero) le stanno sopra, in sovrimpressione:
 *   la pagina non cresce con la mappa, e niente sparisce fuori schermo.
 * - **La rotella zooma, la pagina no.** L'evento `wheel` di React è passivo e
 *   non si può fermare: la pagina scorreva mentre la mappa ingrandiva. Qui lo
 *   si ascolta a mano, non passivo, e lo si trattiene.
 * - **Zoom semantico.** Si ingrandisce lo *spazio*, non il disegno: i nodi si
 *   allontanano, ma cerchi, tratti ed etichette restano della loro misura — si
 *   ingrandisce per leggere, e un testo che cresce copre quello che c'era da
 *   leggere. Le seconde righe compaiono solo quando c'è posto.
 * - **I nodi scivolano.** Cambiare vista, filtrare o escludere non fa saltare
 *   la mappa: ogni nodo va dalla posizione vecchia alla nuova in un attimo, e
 *   l'occhio lo segue. È la differenza fra «la mappa si è riordinata» e «la
 *   mappa è un'altra».
 * - **Chi è vicino a chi.** Il passaggio sopra un nodo accende i suoi archi e
 *   i suoi vicini e spegne il resto: in una matassa è l'unico modo di leggere
 *   un nodo alla volta.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { t } from '../i18n/lingua'
import {
  LAYOUTS,
  posiziona,
  type Fascia,
  type LayoutMappa,
  type Posizione,
} from './mappaLayout'

export type GruppoNodo =
  | 'rete'
  | 'firewall'
  | 'wireless'
  | 'server'
  | 'virtuale'
  | 'cluster'
  | 'storage'
  | 'cloud'
  | 'esterno'
  | 'medicale'
  | 'sconosciuto'

export interface NodoMappa {
  id: string
  label: string
  /** La seconda riga (un indirizzo, una classe): compare quando c'è posto. */
  sotto?: string | null
  colore: string
  gruppo: GruppoNodo
  /** Il livello del criterio della pagina: la fascia della vista gerarchica. */
  livello: number
  /** Il nodo da cui si guarda la mappa: più grande. */
  seme?: boolean
  /** Corrisponde alla ricerca: un alone. */
  trovato?: boolean
  /** Dismesso o scomparso: disegnato sbiadito, non come gli altri. */
  spento?: boolean
  /** Un segno accanto al nome (es. ⁺ «ha altri vicini»). */
  segno?: string
}

export interface ArcoMappa {
  id: string
  src: string
  dst: string
  colore: string
  tratteggio?: string
  /** Spessore in pixel (1–5): per esempio in proporzione al volume. */
  spessore?: number
  /** Cosa scrivere sull'arco quando lo si guarda. */
  etichetta?: string
  /** Il tooltip. */
  titolo?: string
}

export interface VoceLegenda {
  etichetta: string
  colore: string
  tratteggio?: string
  forma?: 'linea' | 'punto'
}

interface Props {
  nodi: NodoMappa[]
  archi: ArcoMappa[]
  layout: LayoutMappa
  onLayout: (layout: LayoutMappa) => void
  /** Distanza in salti dal centro: guida la vista radiale quando c'è. */
  distanza?: Map<string, number>
  /** I nomi delle fasce della vista gerarchica, per livello. */
  nomiFasce?: (livello: number) => string
  /** Le posizioni decise a mano: vincono sulla disposizione. */
  fissati?: Map<string, Posizione>
  onFissa?: (id: string, p: Posizione) => void
  selezionato?: string | null
  arcoScelto?: string | null
  onClickNodo?: (id: string, event: React.MouseEvent) => void
  onDoppioClickNodo?: (id: string) => void
  onMenuNodo?: (id: string, x: number, y: number) => void
  onClickArco?: (id: string) => void
  onMenuArco?: (id: string, x: number, y: number) => void
  onClickSfondo?: () => void
  /** Nodi da inquadrare quando cambiano (un arco scelto, un risultato). */
  inquadra?: string[]
  legenda?: VoceLegenda[]
  etichetta: string
  /** Altezza della tela (px); in schermo intero prende tutto. */
  altezza?: number
}

type Vista = { x: number; y: number; k: number }

const RAGGIO = 17
const RAGGIO_SEME = 23
const ZOOM_MIN = 0.12
const ZOOM_MAX = 3.5
const DURATA = 480

function morbido(p: number): number {
  return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2
}

function scorcia(testo: string, max: number): string {
  return testo.length > max ? testo.slice(0, max - 1) + '…' : testo
}

export default function Mappa({
  nodi,
  archi,
  layout,
  onLayout,
  distanza,
  nomiFasce,
  fissati,
  onFissa,
  selezionato,
  arcoScelto,
  onClickNodo,
  onDoppioClickNodo,
  onMenuNodo,
  onClickArco,
  onMenuArco,
  onClickSfondo,
  inquadra,
  legenda,
  etichetta,
  altezza = 620,
}: Props) {
  const contenitore = useRef<HTMLDivElement | null>(null)
  const [misura, setMisura] = useState({ w: 900, h: altezza })
  const [vista, setVista] = useState<Vista>({ x: 0, y: 0, k: 1 })
  const vistaRef = useRef(vista)
  vistaRef.current = vista
  const [sopra, setSopra] = useState<string | null>(null)
  const [schermoIntero, setSchermoIntero] = useState(false)

  // --- La disposizione: si ricalcola solo quando cambia la STRUTTURA ---

  const firma = useMemo(
    () =>
      nodi.map((n) => `${n.id}:${n.livello}`).join('|') +
      '#' +
      archi.map((a) => `${a.src}>${a.dst}`).join('|') +
      '#' +
      layout +
      '#' +
      (distanza ? [...distanza.entries()].map(([k, v]) => k + v).join('') : ''),
    [nodi, archi, layout, distanza],
  )
  const disposizione = useMemo(
    () =>
      posiziona(
        nodi.map((n) => ({ id: n.id, level: n.livello, ordine: n.label })),
        archi.map((a) => ({ src: a.src, dst: a.dst })),
        layout,
        layout === 'radiale' ? distanza : undefined,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firma],
  )
  const bersaglio = useMemo(() => {
    if (!fissati || fissati.size === 0) return disposizione.posizioni
    const unite = new Map(disposizione.posizioni)
    for (const [id, p] of fissati) if (unite.has(id)) unite.set(id, p)
    return unite
  }, [disposizione, fissati])

  // Quanto spazio ha il nome di ogni nodo: la distanza dal vicino più
  // prossimo (in pixel a zoom 1). Il nome si accorcia a quella misura, così
  // non si sovrappone mai a quello accanto, a qualunque zoom e in ogni vista.
  const spazio = useMemo(() => {
    const out = new Map<string, number>()
    const punti = [...bersaglio.entries()]
    for (let i = 0; i < punti.length; i++) {
      let minimo = Infinity
      const [, a] = punti[i]
      for (let j = 0; j < punti.length; j++) {
        if (i === j) continue
        const b = punti[j][1]
        // Un vicino sopra o sotto disturba meno di uno di fianco: il nome è
        // largo, non alto. La distanza verticale pesa un terzo.
        const dx = Math.abs(a.x - b.x)
        const dy = Math.abs(a.y - b.y)
        if (dy > 60) continue
        minimo = Math.min(minimo, dx + dy / 3)
      }
      out.set(punti[i][0], minimo)
    }
    return out
  }, [bersaglio])

  // --- Il movimento: dalle posizioni mostrate alle nuove ---

  const [mostrate, setMostrate] = useState<Map<string, Posizione>>(bersaglio)
  const mostrateRef = useRef(mostrate)
  mostrateRef.current = mostrate
  const animazione = useRef<number | null>(null)
  const trascinando = useRef<string | null>(null)

  useEffect(() => {
    // Durante il trascinamento di un nodo niente animazione: il nodo deve
    // stare sotto il dito, non inseguirlo.
    if (trascinando.current) {
      setMostrate(bersaglio)
      return
    }
    const da = mostrateRef.current
    const inizio = performance.now()
    const centro = centroDi(bersaglio)
    if (animazione.current) cancelAnimationFrame(animazione.current)
    const passo = (ora: number) => {
      const p = morbido(Math.min(1, (ora - inizio) / DURATA))
      const frame = new Map<string, Posizione>()
      for (const [id, fine] of bersaglio) {
        // Un nodo nuovo nasce dal centro della mappa, non dal nulla.
        const partenza = da.get(id) ?? centro
        frame.set(id, {
          x: partenza.x + (fine.x - partenza.x) * p,
          y: partenza.y + (fine.y - partenza.y) * p,
        })
      }
      setMostrate(frame)
      if (p < 1) animazione.current = requestAnimationFrame(passo)
      else animazione.current = null
    }
    animazione.current = requestAnimationFrame(passo)
    return () => {
      if (animazione.current) cancelAnimationFrame(animazione.current)
    }
  }, [bersaglio])

  // --- La misura della tela ---

  useEffect(() => {
    const el = contenitore.current
    if (!el) return
    const osserva = new ResizeObserver(() => {
      const r = el.getBoundingClientRect()
      setMisura({ w: Math.max(320, r.width), h: Math.max(280, r.height) })
    })
    osserva.observe(el)
    return () => osserva.disconnect()
  }, [])

  useEffect(() => {
    const cambia = () => setSchermoIntero(document.fullscreenElement === contenitore.current)
    document.addEventListener('fullscreenchange', cambia)
    return () => document.removeEventListener('fullscreenchange', cambia)
  }, [])

  // --- L'inquadratura, con il suo movimento ---

  // Un'inquadratura alla volta: la nuova annulla quella in corso. Due animazioni
  // sovrapposte finivano in ordine casuale, e vinceva quella sbagliata.
  const animazioneVista = useRef<number | null>(null)
  const animaVista = useCallback((verso: Vista) => {
    if (animazioneVista.current) cancelAnimationFrame(animazioneVista.current)
    const da = vistaRef.current
    const inizio = performance.now()
    const passo = (ora: number) => {
      const p = morbido(Math.min(1, (ora - inizio) / 380))
      setVista({
        x: da.x + (verso.x - da.x) * p,
        y: da.y + (verso.y - da.y) * p,
        k: da.k + (verso.k - da.k) * p,
      })
      animazioneVista.current = p < 1 ? requestAnimationFrame(passo) : null
    }
    animazioneVista.current = requestAnimationFrame(passo)
  }, [])
  // Finché nessuno ha toccato la mappa, l'inquadratura si applica secca:
  // la misura vera della tela arriva un istante dopo la prima, e animare da
  // una misura provvisoria è solo un sussulto.
  const toccata = useRef(false)

  // Con le fasce si tiene libera una colonna a sinistra per i loro nomi.
  const gronda = layout === 'gerarchico' && !!nomiFasce ? 150 : 0
  const vistaPer = useCallback(
    (punti: Posizione[], massimo = 1.4): Vista => {
      if (!punti.length) return { x: misura.w / 2, y: misura.h / 2, k: 1 }
      let x0 = Infinity
      let y0 = Infinity
      let x1 = -Infinity
      let y1 = -Infinity
      for (const p of punti) {
        x0 = Math.min(x0, p.x)
        y0 = Math.min(y0, p.y)
        x1 = Math.max(x1, p.x)
        y1 = Math.max(y1, p.y)
      }
      const margine = 90
      const larghezza = misura.w - gronda
      const k = Math.max(
        ZOOM_MIN,
        Math.min(
          massimo,
          (larghezza - margine * 2) / Math.max(1, x1 - x0),
          (misura.h - margine * 2) / Math.max(1, y1 - y0),
        ),
      )
      return {
        k,
        x: gronda + larghezza / 2 - ((x0 + x1) / 2) * k,
        y: misura.h / 2 - ((y0 + y1) / 2) * k,
      }
    },
    [misura, gronda],
  )

  const adatta = useCallback(() => {
    animaVista(vistaPer([...bersaglio.values()]))
  }, [animaVista, vistaPer, bersaglio])

  // Si riadatta quando cambia la struttura o la misura: non quando si fissa un
  // nodo, che deve restare dove si sta guardando.
  useEffect(() => {
    const v = vistaPer([...disposizione.posizioni.values()])
    if (!toccata.current) {
      if (animazioneVista.current) cancelAnimationFrame(animazioneVista.current)
      setVista(v)
    } else {
      animaVista(v)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disposizione, misura.w, misura.h])

  const chiaveInquadra = (inquadra ?? []).join('|')
  useEffect(() => {
    if (!inquadra?.length) return
    const punti = inquadra.map((id) => bersaglio.get(id)).filter((p): p is Posizione => !!p)
    if (punti.length) animaVista(vistaPer(punti, 1.2))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiaveInquadra])

  // --- Zoom con la rotella: non passivo, la pagina resta ferma ---

  const zoomAttorno = useCallback((fattore: number, cx: number, cy: number) => {
    toccata.current = true
    if (animazioneVista.current) cancelAnimationFrame(animazioneVista.current)
    setVista((v) => {
      const k = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.k * fattore))
      const f = k / v.k
      return { k, x: cx - (cx - v.x) * f, y: cy - (cy - v.y) * f }
    })
  }, [])

  useEffect(() => {
    const el = contenitore.current
    if (!el) return
    const rotella = (e: WheelEvent) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      // Il pizzico sul trackpad arriva come rotella con ctrl: più sensibile.
      const intensita = e.ctrlKey ? 0.012 : 0.0018
      zoomAttorno(Math.exp(-e.deltaY * intensita), e.clientX - r.left, e.clientY - r.top)
    }
    el.addEventListener('wheel', rotella, { passive: false })
    return () => el.removeEventListener('wheel', rotella)
  }, [zoomAttorno])

  // --- Spostare la tela e i nodi ---

  const gesto = useRef<
    | { tipo: 'tela'; x: number; y: number; da: Vista; mosso: boolean }
    | { tipo: 'nodo'; id: string; x: number; y: number; da: Posizione; mosso: boolean }
    | null
  >(null)
  const nodoMosso = useRef(false)

  function giuTela(e: React.PointerEvent<SVGSVGElement>) {
    if (e.button !== 0) return
    toccata.current = true
    if (animazioneVista.current) cancelAnimationFrame(animazioneVista.current)
    gesto.current = { tipo: 'tela', x: e.clientX, y: e.clientY, da: vista, mosso: false }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function giuNodo(e: React.PointerEvent<SVGGElement>, id: string) {
    if (e.button !== 0) return
    e.stopPropagation()
    const da = bersaglio.get(id)
    if (!da) return
    gesto.current = { tipo: 'nodo', id, x: e.clientX, y: e.clientY, da, mosso: false }
    nodoMosso.current = false
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function muovi(e: React.PointerEvent) {
    const g = gesto.current
    if (!g) return
    const dx = e.clientX - g.x
    const dy = e.clientY - g.y
    if (!g.mosso && Math.abs(dx) + Math.abs(dy) < 4) return
    g.mosso = true
    if (g.tipo === 'tela') {
      setVista({ ...g.da, x: g.da.x + dx, y: g.da.y + dy })
    } else if (onFissa) {
      nodoMosso.current = true
      trascinando.current = g.id
      onFissa(g.id, { x: g.da.x + dx / vista.k, y: g.da.y + dy / vista.k })
    }
  }

  function su(e: React.PointerEvent) {
    const g = gesto.current
    gesto.current = null
    trascinando.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    if (g?.tipo === 'tela' && !g.mosso) onClickSfondo?.()
  }

  // --- Tastiera: + − 0 ---

  function tasto(e: React.KeyboardEvent) {
    if (e.key === '+' || e.key === '=') zoomAttorno(1.25, misura.w / 2, misura.h / 2)
    else if (e.key === '-') zoomAttorno(0.8, misura.w / 2, misura.h / 2)
    else if (e.key === '0') adatta()
    else return
    e.preventDefault()
  }

  function schermo() {
    const el = contenitore.current
    if (!el) return
    if (document.fullscreenElement) void document.exitFullscreen()
    else void el.requestFullscreen?.()
  }

  // --- Chi è acceso ---

  const vicini = useMemo(() => {
    const out = new Map<string, Set<string>>()
    for (const a of archi) {
      if (!out.has(a.src)) out.set(a.src, new Set())
      if (!out.has(a.dst)) out.set(a.dst, new Set())
      out.get(a.src)!.add(a.dst)
      out.get(a.dst)!.add(a.src)
    }
    return out
  }, [archi])

  const fuoco = sopra ?? selezionato ?? null
  const arcoFuoco = arcoScelto ? archi.find((a) => a.id === arcoScelto) : undefined
  const acceso = (id: string): boolean => {
    if (arcoFuoco) return id === arcoFuoco.src || id === arcoFuoco.dst
    if (!fuoco) return true
    return id === fuoco || (vicini.get(fuoco)?.has(id) ?? false)
  }
  const arcoAcceso = (a: ArcoMappa): boolean => {
    if (arcoFuoco) return a.id === arcoFuoco.id
    if (!fuoco) return true
    return a.src === fuoco || a.dst === fuoco
  }

  // --- Dal mondo allo schermo ---

  const sx = (p: Posizione) => p.x * vista.k + vista.x
  const sy = (p: Posizione) => p.y * vista.k + vista.y

  // Gli archi fra gli stessi due nodi si aprono a ventaglio.
  const ventagli = useMemo(() => {
    const gruppi = new Map<string, ArcoMappa[]>()
    for (const a of archi) {
      const chiave = a.src < a.dst ? `${a.src}|${a.dst}` : `${a.dst}|${a.src}`
      const g = gruppi.get(chiave)
      if (g) g.push(a)
      else gruppi.set(chiave, [a])
    }
    const out = new Map<string, { indice: number; totale: number }>()
    for (const g of gruppi.values()) g.forEach((a, i) => out.set(a.id, { indice: i, totale: g.length }))
    return out
  }, [archi])

  const seconde = vista.k >= 0.85 && nodi.length <= 250
  /** Quanti caratteri del nome ci stanno, dato lo spazio fino al vicino. */
  const caratteri = (id: string): number => {
    const d = spazio.get(id) ?? Infinity
    if (!isFinite(d)) return 26
    return Math.min(26, Math.floor((d * vista.k - 10) / 6.4))
  }

  const fasce: Fascia[] = layout === 'gerarchico' ? disposizione.fasce : []
  const anelli = layout === 'radiale' ? disposizione.anelli ?? [] : []
  const nomeAnello = (a: { livello?: number; salti?: number }): string =>
    a.salti !== undefined
      ? a.salti === 1
        ? t('1 salto')
        : t('{n} salti', { n: a.salti })
      : a.livello !== undefined && nomiFasce
        ? nomiFasce(a.livello)
        : ''

  return (
    <div
      ref={contenitore}
      className={`mappa${schermoIntero ? ' mappa--intera' : ''}`}
      style={{ height: schermoIntero ? undefined : altezza }}
      tabIndex={0}
      onKeyDown={tasto}
      aria-label={etichetta}
    >
      <svg
        className="mappa__tela"
        width={misura.w}
        height={misura.h}
        role="img"
        aria-label={etichetta}
        onPointerDown={giuTela}
        onPointerMove={muovi}
        onPointerUp={su}
        onPointerCancel={su}
      >
        {/* Le fasce della vista gerarchica: il criterio si legge a sinistra. */}
        {fasce.map((f, i) => {
          // La fascia va da metà strada con quella sopra a metà strada con
          // quella sotto: così le fasce si toccano senza sovrapporsi, a
          // qualunque zoom, e il nome sta dentro la sua.
          const sopraF = fasce[i - 1]
          const sottoF = fasce[i + 1]
          const w0 = sopraF ? (sopraF.y1 + f.y0) / 2 : f.y0 - 66
          const w1 = sottoF ? (f.y1 + sottoF.y0) / 2 : f.y1 + 66
          const y0 = w0 * vista.k + vista.y
          const y1 = w1 * vista.k + vista.y
          return (
            <g key={f.livello} className="mappa__fascia">
              <rect
                x={0}
                y={y0}
                width={misura.w}
                height={Math.max(20, y1 - y0)}
                className={i % 2 === 0 ? 'mappa__fascia-a' : 'mappa__fascia-b'}
              />
              {nomiFasce && (
                <g transform={`translate(12 ${(y0 + y1) / 2})`}>
                  <rect
                    x={0}
                    y={-11}
                    height={22}
                    width={nomiFasce(f.livello).length * 7.4 + 18}
                    rx={11}
                    className="mappa__fascia-pillola"
                  />
                  <text x={9} y={0} dominantBaseline="central" className="mappa__fascia-nome">
                    {nomiFasce(f.livello)}
                  </text>
                </g>
              )}
            </g>
          )
        })}

        {/* Gli anelli della vista radiale: il criterio si legge in alto. */}
        {anelli.map((a) => {
          const r = a.raggio * vista.k
          const nome = nomeAnello(a)
          return (
            <g key={a.raggio} className="mappa__anello">
              <circle cx={vista.x} cy={vista.y} r={r} className="mappa__anello-cerchio" />
              {nome && (
                <g
                  transform={`translate(${vista.x + r * Math.cos(a.angoloNome)} ${vista.y + r * Math.sin(a.angoloNome)})`}
                >
                  <rect
                    x={-(nome.length * 7.4 + 18) / 2}
                    y={-11}
                    height={22}
                    width={nome.length * 7.4 + 18}
                    rx={11}
                    className="mappa__fascia-pillola"
                  />
                  <text x={0} y={0} textAnchor="middle" dominantBaseline="central" className="mappa__fascia-nome">
                    {nome}
                  </text>
                </g>
              )}
            </g>
          )
        })}

        {/* Prima gli archi, così i nodi restano sopra. */}
        {archi.map((a) => {
          const p1 = mostrate.get(a.src)
          const p2 = mostrate.get(a.dst)
          if (!p1 || !p2) return null
          const x1 = sx(p1)
          const y1 = sy(p1)
          const x2 = sx(p2)
          const y2 = sy(p2)
          const { indice, totale } = ventagli.get(a.id) ?? { indice: 0, totale: 1 }
          // Il ventaglio: la curvatura cresce con l'indice, simmetrica.
          const scarto = (indice - (totale - 1) / 2) * 22
          const lx = x2 - x1
          const ly = y2 - y1
          const l = Math.sqrt(lx * lx + ly * ly) || 1
          // La normale va sempre dallo stesso lato per la coppia, qualunque
          // sia il verso dell'arco: altrimenti due archi opposti si
          // sovrappongono invece di aprirsi.
          const segno = a.src < a.dst ? 1 : -1
          const nx = (-ly / l) * segno
          const ny = (lx / l) * segno
          const mx = (x1 + x2) / 2 + nx * scarto
          const my = (y1 + y2) / 2 + ny * scarto
          const cx = (x1 + x2) / 2 + nx * scarto * 2
          const cy = (y1 + y2) / 2 + ny * scarto * 2
          const d = totale === 1 ? `M${x1},${y1} L${x2},${y2}` : `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`
          const scelto = a.id === arcoScelto
          const vivo = arcoAcceso(a)
          const spessore = a.spessore ?? 1.6
          const mostraEtichetta =
            !!a.etichetta && (scelto || (vivo && (fuoco || arcoFuoco)) || (totale > 1 && vista.k >= 0.9))
          return (
            <g key={a.id} className="mappa__arco" opacity={vivo ? 1 : 0.12}>
              <path
                d={d}
                fill="none"
                stroke="transparent"
                strokeWidth={14}
                style={{ cursor: onClickArco ? 'pointer' : undefined }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation()
                  onClickArco?.(a.id)
                }}
                onContextMenu={(e) => {
                  if (!onMenuArco) return
                  e.preventDefault()
                  e.stopPropagation()
                  onMenuArco(a.id, e.clientX, e.clientY)
                }}
              >
                {a.titolo && <title>{a.titolo}</title>}
              </path>
              <path
                d={d}
                fill="none"
                stroke={scelto ? 'var(--warn)' : a.colore}
                strokeWidth={scelto ? spessore + 2 : fuoco && vivo ? spessore + 0.8 : spessore}
                strokeDasharray={a.tratteggio}
                strokeLinecap="round"
                style={{ pointerEvents: 'none' }}
              />
              {mostraEtichetta && (
                <text
                  x={mx}
                  y={my}
                  className={`mappa__arco-etichetta${scelto ? ' mappa__arco-etichetta--scelto' : ''}`}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {scorcia(a.etichetta!, 34)}
                </text>
              )}
            </g>
          )
        })}

        {nodi.map((n) => {
          const p = mostrate.get(n.id)
          if (!p) return null
          const x = sx(p)
          const y = sy(p)
          const r = n.seme ? RAGGIO_SEME : RAGGIO
          const vivo = acceso(n.id)
          const scelto = n.id === selezionato
          const fissato = fissati?.has(n.id) ?? false
          const inEvidenza = scelto || n.id === sopra
          const massimo = inEvidenza ? 40 : caratteri(n.id)
          const nome = scorcia(n.label + (n.segno ? ` ${n.segno}` : ''), Math.max(4, massimo))
          const mostraNome = massimo >= 5 || inEvidenza || n.seme || n.trovato
          const mostraSotto = !!n.sotto && ((seconde && massimo >= 12) || inEvidenza)
          return (
            <g
              key={n.id}
              className={`mappa__nodo${n.spento ? ' mappa__nodo--spento' : ''}`}
              transform={`translate(${x} ${y})`}
              opacity={vivo ? 1 : 0.18}
              onPointerDown={(e) => giuNodo(e, n.id)}
              onPointerMove={(e) => {
                e.stopPropagation()
                muovi(e)
              }}
              onPointerUp={(e) => {
                e.stopPropagation()
                su(e)
              }}
              onPointerCancel={(e) => {
                e.stopPropagation()
                su(e)
              }}
              onPointerEnter={() => setSopra(n.id)}
              onPointerLeave={() => setSopra((s) => (s === n.id ? null : s))}
              onClick={(e) => {
                e.stopPropagation()
                if (nodoMosso.current) {
                  nodoMosso.current = false
                  return
                }
                onClickNodo?.(n.id, e)
              }}
              onDoubleClick={(e) => {
                e.stopPropagation()
                onDoppioClickNodo?.(n.id)
              }}
              onContextMenu={(e) => {
                if (!onMenuNodo) return
                e.preventDefault()
                e.stopPropagation()
                onMenuNodo(n.id, e.clientX, e.clientY)
              }}
              style={{ cursor: onFissa ? 'grab' : 'pointer' }}
            >
              <title>{[n.label, n.sotto].filter(Boolean).join(' · ')}</title>
              {n.trovato && <circle r={r + 9} className="mappa__alone" />}
              {scelto && <circle r={r + 5} fill="none" stroke="var(--text)" strokeWidth={2.5} />}
              {fissato && (
                <circle r={r + 4} fill="none" stroke="var(--text-muted)" strokeWidth={1.5} strokeDasharray="3 3" />
              )}
              <circle r={r} fill={n.colore} className="mappa__disco" />
              <Glifo gruppo={n.gruppo} scala={n.seme ? 1.25 : 1} />
              {mostraNome && (
                <text y={r + 15} textAnchor="middle" className="mappa__nome">
                  {nome}
                </text>
              )}
              {mostraNome && mostraSotto && (
                <text y={r + 29} textAnchor="middle" className="mappa__sotto">
                  {scorcia(n.sotto!, 30)}
                </text>
              )}
            </g>
          )
        })}
      </svg>

      {/* I comandi, sopra la tela: sempre a portata, qualunque sia lo zoom. */}
      <div className="mappa__viste" role="group" aria-label={t('Disposizione')}>
        {LAYOUTS.map((v) => (
          <button
            key={v.key}
            type="button"
            className={layout === v.key ? 'mappa__vista mappa__vista--attiva' : 'mappa__vista'}
            title={v.hint()}
            onClick={() => {
              toccata.current = true
              onLayout(v.key)
            }}
          >
            <IconaVista vista={v.key} />
            {v.label()}
          </button>
        ))}
      </div>

      <div className="mappa__comandi">
        <button type="button" onClick={() => zoomAttorno(1.25, misura.w / 2, misura.h / 2)} title={t('Ingrandisci (+)')} aria-label={t('Ingrandisci (+)')}>+</button>
        <button type="button" onClick={() => zoomAttorno(0.8, misura.w / 2, misura.h / 2)} title={t('Riduci (−)')} aria-label={t('Riduci (−)')}>−</button>
        <button type="button" onClick={adatta} title={t('Adatta alla finestra (0)')} aria-label={t('Adatta alla finestra (0)')}>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden><path d="M1 5V1h4M9 1h4v4M13 9v4H9M5 13H1V9" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
        </button>
        <button type="button" onClick={schermo} title={t('Schermo intero')} aria-label={t('Schermo intero')}>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
            {schermoIntero ? (
              <path d="M5 1v4H1M9 1v4h4M13 9H9v4M1 9h4v4" fill="none" stroke="currentColor" strokeWidth="1.6" />
            ) : (
              <path d="M1 4V1h3M10 1h3v3M13 10v3h-3M4 13H1v-3" fill="none" stroke="currentColor" strokeWidth="1.6" />
            )}
          </svg>
        </button>
      </div>

      {legenda && legenda.length > 0 && (
        <div className="mappa__legenda">
          {legenda.map((v) => (
            <span key={v.etichetta}>
              {v.forma === 'punto' ? (
                <svg width="10" height="10" aria-hidden><circle cx="5" cy="5" r="5" fill={v.colore} /></svg>
              ) : (
                <svg width="26" height="8" aria-hidden>
                  <line x1="0" y1="4" x2="26" y2="4" stroke={v.colore} strokeWidth="2.5" strokeDasharray={v.tratteggio} />
                </svg>
              )}
              {v.etichetta}
            </span>
          ))}
        </div>
      )}

      <Minimappa
        nodi={nodi}
        posizioni={mostrate}
        vista={vista}
        misura={misura}
        onCentra={(p) =>
          animaVista({ k: vista.k, x: misura.w / 2 - p.x * vista.k, y: misura.h / 2 - p.y * vista.k })
        }
      />
    </div>
  )
}

function centroDi(posizioni: Map<string, Posizione>): Posizione {
  let x = 0
  let y = 0
  let n = 0
  for (const p of posizioni.values()) {
    x += p.x
    y += p.y
    n++
  }
  return n ? { x: x / n, y: y / n } : { x: 0, y: 0 }
}

/**
 * La minimappa: tutta la mappa in piccolo, con il riquadro di ciò che si sta
 * guardando. Un clic ci porta lì. Compare quando la mappa non sta tutta nella
 * finestra: se si vede già tutto, non aggiunge niente.
 */
function Minimappa({
  nodi,
  posizioni,
  vista,
  misura,
  onCentra,
}: {
  nodi: NodoMappa[]
  posizioni: Map<string, Posizione>
  vista: Vista
  misura: { w: number; h: number }
  onCentra: (p: Posizione) => void
}) {
  const W = 168
  const H = 112
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const p of posizioni.values()) {
    x0 = Math.min(x0, p.x)
    y0 = Math.min(y0, p.y)
    x1 = Math.max(x1, p.x)
    y1 = Math.max(y1, p.y)
  }
  if (!isFinite(x0) || nodi.length < 8) return null
  // Ciò che si vede, in coordinate del mondo.
  const vx0 = -vista.x / vista.k
  const vy0 = -vista.y / vista.k
  const vx1 = vx0 + misura.w / vista.k
  const vy1 = vy0 + misura.h / vista.k
  if (vx0 <= x0 && vy0 <= y0 && vx1 >= x1 && vy1 >= y1) return null

  const pad = 40
  const bx0 = x0 - pad
  const by0 = y0 - pad
  const scala = Math.min(W / (x1 - x0 + pad * 2 || 1), H / (y1 - y0 + pad * 2 || 1))
  const m = (v: number, o: number) => (v - o) * scala
  return (
    <svg
      className="mappa__mini"
      width={W}
      height={H}
      onPointerDown={(e) => {
        e.stopPropagation()
        const r = e.currentTarget.getBoundingClientRect()
        onCentra({ x: (e.clientX - r.left) / scala + bx0, y: (e.clientY - r.top) / scala + by0 })
      }}
      aria-hidden
    >
      {nodi.map((n) => {
        const p = posizioni.get(n.id)
        if (!p) return null
        return <circle key={n.id} cx={m(p.x, bx0)} cy={m(p.y, by0)} r={2} fill={n.colore} />
      })}
      <rect
        x={m(vx0, bx0)}
        y={m(vy0, by0)}
        width={(vx1 - vx0) * scala}
        height={(vy1 - vy0) * scala}
        className="mappa__mini-vista"
      />
    </svg>
  )
}

/** L'icona dentro il disco: dice cos'è l'oggetto prima del nome. */
function Glifo({ gruppo, scala }: { gruppo: GruppoNodo; scala: number }) {
  const tratto = {
    fill: 'none',
    stroke: '#fff',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  let corpo: ReactNode
  switch (gruppo) {
    case 'rete':
      corpo = <path {...tratto} d="M-6-2.5H5M2.5-5 5-2.5 2.5 0M6 2.5H-5M-2.5 0-5 2.5-2.5 5" />
      break
    case 'firewall':
      corpo = <path {...tratto} d="M0-6.5 5.5-4.2V0c0 3.3-2.4 5.6-5.5 6.5C-3.1 5.6-5.5 3.3-5.5 0v-4.2Z" />
      break
    case 'wireless':
      corpo = (
        <>
          <path {...tratto} d="M-6-1.5a8.5 8.5 0 0 1 12 0M-3.8 1a5 5 0 0 1 7.6 0" />
          <circle cx={0} cy={4} r={1.4} fill="#fff" />
        </>
      )
      break
    case 'server':
      corpo = (
        <>
          <rect {...tratto} x={-5.5} y={-6} width={11} height={5} rx={1} />
          <rect {...tratto} x={-5.5} y={1} width={11} height={5} rx={1} />
          <circle cx={-3} cy={-3.5} r={0.9} fill="#fff" />
          <circle cx={-3} cy={3.5} r={0.9} fill="#fff" />
        </>
      )
      break
    case 'virtuale':
      corpo = (
        <>
          <rect {...tratto} x={-6} y={-6} width={12} height={12} rx={2} strokeDasharray="2.2 1.8" />
          <rect {...tratto} x={-2.8} y={-2.8} width={5.6} height={5.6} rx={1} />
        </>
      )
      break
    case 'cluster':
      corpo = (
        <>
          <circle {...tratto} cx={0} cy={-3.6} r={2.6} />
          <circle {...tratto} cx={-3.8} cy={3} r={2.6} />
          <circle {...tratto} cx={3.8} cy={3} r={2.6} />
        </>
      )
      break
    case 'storage':
      corpo = (
        <>
          <ellipse {...tratto} cx={0} cy={-4.2} rx={5.5} ry={2} />
          <path {...tratto} d="M-5.5-4.2v8.4c0 1.1 2.5 2 5.5 2s5.5-.9 5.5-2v-8.4M-5.5 0c0 1.1 2.5 2 5.5 2s5.5-.9 5.5-2" />
        </>
      )
      break
    case 'cloud':
      corpo = <path {...tratto} d="M-4 4.5h8.2a3.3 3.3 0 0 0 .3-6.6 4.6 4.6 0 0 0-8.9.9A2.9 2.9 0 0 0-4 4.5Z" />
      break
    case 'medicale':
      // A cross with a pulse: an electromedical device.
      corpo = <path {...tratto} d="M-6 0.5h3l1.6-4 2.6 7.5 1.8-3.5H6" />
      break
    case 'esterno':
      corpo = (
        <>
          <circle {...tratto} cx={0} cy={0} r={6} />
          <path {...tratto} d="M-6 0h12M0-6c2.2 2.2 2.2 9.8 0 12M0-6c-2.2 2.2-2.2 9.8 0 12" />
        </>
      )
      break
    default:
      corpo = (
        <text textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700} fill="#fff">
          ?
        </text>
      )
  }
  return (
    <g transform={`scale(${scala})`} style={{ pointerEvents: 'none' }}>
      {corpo}
    </g>
  )
}

function IconaVista({ vista }: { vista: LayoutMappa }) {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 }
  if (vista === 'gerarchico') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
        <circle cx="7" cy="2.5" r="1.8" {...p} />
        <circle cx="3" cy="11.5" r="1.8" {...p} />
        <circle cx="11" cy="11.5" r="1.8" {...p} />
        <path d="M7 4.3v2.7M3 9.7V7h8v2.7" {...p} />
      </svg>
    )
  }
  if (vista === 'radiale') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
        <circle cx="7" cy="7" r="1.8" {...p} />
        <circle cx="7" cy="7" r="5.5" {...p} strokeDasharray="2 1.6" />
      </svg>
    )
  }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
      <circle cx="3" cy="4" r="1.8" {...p} />
      <circle cx="11" cy="3" r="1.8" {...p} />
      <circle cx="7" cy="11" r="1.8" {...p} />
      <path d="M4.6 4.6 6 9.4M9.6 4.3 8 9.4M4.8 3.8h4.4" {...p} />
    </svg>
  )
}
